import { type AsyncListLoadOptions } from "@react-stately/data";
import debounce from "awesome-debounce-promise";
import pluralize from "pluralize";
import { type FC, type Key, useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Item, Section, useAsyncList } from "react-stately";
import { titleCase } from "title-case";
import { useDebounce } from "use-debounce";

import { MenuOptionItem, MenuOptionItemIntent, SearchAutosuggest, Tooltip, TooltipIntent, useTooltip } from "widgets";

import { escapeRegexCharacters } from "../../../app-utils/string.js";
import { showNavbarElement } from "../../../redux/modules/navbar.js";
import { createSearchResultAndRedirect } from "../../../redux/modules/search-results.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { Hotkeys } from "../../../settings/hotkeys.js";
import { FEATURE_DISPLAY_NAME } from "../../../settings/settings.js";
import {
  type QuickSearchSuggestionsCategory,
  type QuickSearchSuggestionsLazyResult,
  useQuickSearchSuggestionsLazy,
} from "./graphql/useQuickSearchGraphql.js";
import { globalSearchSuggestionSelect } from "@/redux/modules/table-context.ts";

export enum ItemType {
  Suggestion = 1,
  Section = 2,
  EmptyResults = 3,
  ViewAllResults = 4,
  AdvancedSearch = 5,
}

type Category = QuickSearchSuggestionsLazyResult & {
  title?: string;
  items: Array<NonNullable<Item>>;
};

type Item = QuickSearchSuggestionsCategory & {
  itemType?: ItemType;
  highlight?: boolean;
};

export const GlobalSearch: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [search] = useQuickSearchSuggestionsLazy();
  const [generateResults, setGenerateResults] = useState(false);

  const debouncedLoad = useMemo(
    () =>
      debounce(async function load({ filterText }: AsyncListLoadOptions<Category, string>) {
        if (filterText != undefined && filterText.trim() !== "") {
          if (filterText.trim().length < 3) {
            return { items: [] };
          }

          const response = await search({ searchTerm: filterText });
          const responseSearchTerm = response?.data?.search?.globalSearch?.searchTerm;

          if (filterText === responseSearchTerm) {
            const results = response.data?.search?.globalSearch?.categories;

            if (results && results.length > 0) {
              const total = results.reduce((previous, next) => {
                return previous + (next?.totalItemCount ?? 0);
              }, 0);

              return {
                items: [
                  ...results.map((result: NonNullable<QuickSearchSuggestionsLazyResult>) => {
                    const sectionName = titleCase(FEATURE_DISPLAY_NAME[result.categoryType!]);

                    return {
                      ...result,
                      title: result?.categoryName ?? pluralize(sectionName, result?.totalItemCount ?? 0),
                      items: [
                        ...(result?.items?.map((item) => ({
                          itemType: ItemType.Suggestion,
                          ...item,
                        })) ?? []),
                        {
                          itemType: ItemType.Section,
                          id: `${result?.categoryType}_${result?.subTypeId}_count`,
                          name: `View All ${sectionName} Results`,
                          description: `${result?.totalItemCount} items found`,
                          highlight: true,
                          categoryType: result?.categoryType,
                          subTypeId: result?.subTypeId,
                        },
                      ] as Item[],
                    };
                  }),
                  {
                    categoryType: "Quick Search",
                    title: "Quick Search",
                    subTypeId: "__global__",
                    categoryName: null,
                    totalItemCount: null,
                    items: [
                      {
                        itemType: ItemType.ViewAllResults,
                        id: "global-link",
                        name: "View All Results",
                        description: `${total} items found`,
                        highlight: true,
                        type: null,
                        subTypeId: null,
                      },
                      {
                        itemType: ItemType.AdvancedSearch,
                        id: "__advanced-search__",
                        name: "Open Advanced Search",
                        highlight: true,
                        type: null,
                        subTypeId: null,
                        description: null,
                      },
                    ],
                  },
                ] satisfies Category[],
              };
            }
          }
        }

        return filterText
          ? {
              items: [
                {
                  categoryType: "Quick Search",
                  subTypeId: "__global__",
                  categoryName: null,
                  totalItemCount: null,
                  items: [
                    {
                      id: "__nothing-found__",
                      name: "No results",
                      description:
                        filterText?.length < 3
                          ? // ? "Every search term should be at least 3 characters"
                            "At least 3 characters"
                          : null,
                      type: null,
                      subTypeId: null,
                    },
                  ],
                },
              ],
            }
          : { items: [] };
      }, 300),
    [],
  );

  const list = useAsyncList<Category>({
    load: debouncedLoad,
  });

  const onSelectionChange = useCallback(
    (key: Key) => {
      if (key !== "unknown") {
        let res;

        for (const item of list.items) {
          for (const entry of item.items) {
            if (String(entry.id) === String(key)) {
              res = entry;
              break;
            }
          }
        }

        if (res == undefined) {
          return;
        }

        if (res.itemType === ItemType.Suggestion) {
          dispatch(globalSearchSuggestionSelect(res.id, res.type));
          list.setFilterText(""); // force to close the menu...
        } else if (res.itemType === ItemType.Section) {
          const entityTypeInput = {
            subTypeId: res.subTypeId,
            type: res.categoryType,
          };
          setGenerateResults(true);
          dispatch(createSearchResultAndRedirect(list.filterText, [entityTypeInput])).finally(() =>
            setGenerateResults(false),
          );
          list.setFilterText(""); // force to close the menu...
        } else if (res.itemType === ItemType.ViewAllResults) {
          const groupTypes = list.items
            .filter((item) => item.subTypeId !== "__global__")
            .map((item) => ({
              subTypeId: item.subTypeId,
              type: item.categoryType,
            }));

          setGenerateResults(true);
          dispatch(createSearchResultAndRedirect(list.filterText, groupTypes)).finally(() => setGenerateResults(false));
          list.setFilterText(""); // force to close the menu...
        } else if (res.itemType === ItemType.AdvancedSearch) {
          dispatch(showNavbarElement({ showAdvancedSearchPanel: true }));
          list.setFilterText(""); // force to close the menu...
        }
      }
    },
    [dispatch, list],
  );

  const [isOpen] = useDebounce(!!list.filterText && list.filterText.length < 3, 700);
  const triggerRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef(null);
  const { state, tooltipProps, triggerProps, arrowProps } = useTooltip(triggerRef, tooltipRef, {
    trigger: "focus",
    placement: "left",
    offset: 32,
    isOpen,
    shouldFlip: false,
    delay: 3000,
  });

  return (
    <>
      <SearchAutosuggest
        placeholder={"Quick search"}
        items={list.items}
        allowsCustomValue
        inputValue={list.filterText}
        onInputChange={list.setFilterText}
        onSelectionChange={onSelectionChange}
        isLoading={list.isLoading || generateResults}
        selectedKey={null} // Nothing is ever selected
        width={250}
        icon={"search"}
        allowsEmptyCollection
        menuTrigger={"focus"}
        domProps={{ "aria-describedby": triggerProps["aria-describedby"] }}
        ref={triggerRef}
        focusShortcut={Hotkeys.search.open}
      >
        {(category: Category) => (
          <Section
            key={`${category?.categoryType}_${category?.subTypeId}`}
            title={category?.title}
            items={category?.items ?? []}
          >
            {(item) => (
              <Item key={item.id} textValue={item.name!}>
                <MenuOptionItem
                  title={
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          item.name?.replace(
                            new RegExp("(" + escapeRegexCharacters(list.filterText) + ")", "gi"),
                            "<strong>$1</strong>",
                          ) ?? "",
                      }}
                    />
                  }
                  description={item.description}
                  intent={item.highlight ? MenuOptionItemIntent.Primary : MenuOptionItemIntent.Default}
                  icon={
                    item.highlight ? (item.itemType === ItemType.AdvancedSearch ? "manage_search" : "table") : undefined
                  }
                />
              </Item>
            )}
          </Section>
        )}
      </SearchAutosuggest>
      {state.isOpen && (
        <Tooltip
          domProps={tooltipProps}
          arrowProps={arrowProps}
          ref={tooltipRef}
          content={"Every search term should be at least 3 characters."}
          intent={TooltipIntent.Info}
        />
      )}
    </>
  );
};
